import { ConfigConsumer } from '@configProvider/ConfigConsumer';
import { TEventBus } from '@eventBus/EventBus';
import { Fragment } from '@fragment/Fragment';
import { IFragmentsFilterConfig } from '@fragmentsFilter/FragmentsFilter.config';
import { FragmentsFilterEvents } from '@fragmentsFilter/FragmentsFilter.events';
import { findBoxesInArrayBuffer, IParsedBox } from '@fragmentsFilter/FragmentsFilter.helpers';
import { FragmentsQueueEvents } from '@fragmentsQueue/FragmentsQueue.events';
import { Logger, TLogger } from '@logger/Logger';
import bind from '@utils/decorators/bind';

import { FragmentsBufferEvents } from '../FragmentsBuffer/FragmentsBuffer.events';

export class FragmentsFilter extends ConfigConsumer<IFragmentsFilterConfig> {
  private readonly _logger: TLogger;
  private readonly _onFragmentReleasedListenerToken: string;

  private _lastKnownInitSectionNumberOfTracks: number | null = null;

  constructor(eventBus: TEventBus) {
    super('fragmentsFilter', eventBus);
    this._logger = Logger(eventBus, 'Doppio - FragmentsFilter');

    this._logger = Logger(this._eventBus, 'Doppio - FragmentsFilter');

    this._onFragmentReleasedListenerToken = this._eventBus.subscribe(
      FragmentsQueueEvents.fragmentsReleased,
      this._onFragmentReleasedFromQueue,
    );
  }

  public destroy(): void {
    super.destroy();

    this._eventBus.unsubscribe(this._onFragmentReleasedListenerToken);
  }

  @bind
  private _onFragmentReleasedFromQueue(fragments: Fragment[]): void {
    const fragmentsToRelease: Fragment[] = [];

    for (const fragment of fragments) {
      if (fragment.isInitSection) {
        this._parseInitSection(fragment);
        fragmentsToRelease.push(fragment);
      } else {
        const isSegmentValid: boolean = this._parseMediaFragment(fragment);
        if (!this.config.filterOutInconsistentTracks || isSegmentValid) {
          fragmentsToRelease.push(fragment);
        }
      }
    }

    this._eventBus.publish(FragmentsFilterEvents.fragmentsReleased, fragmentsToRelease);
  }

  private _parseInitSection(fragment: Fragment): void {
    this._logger.debug('Parsing init section');
    const rootBoxes: IParsedBox[] = findBoxesInArrayBuffer(fragment.data as ArrayBuffer);
    const moovBox: IParsedBox | null = rootBoxes.find(({ name }) => name === 'moov') || null;

    this._logger.verbose(`Found following root boxes: ${rootBoxes.map(({ name }) => name).join(', ')}`);

    if (moovBox) {
      const moovChildBoxes: IParsedBox[] = findBoxesInArrayBuffer(moovBox.payload);
      this._logger.verbose(`Found following child of moov boxes: ${moovChildBoxes.map(({ name }) => name).join(', ')}`);

      const trakBoxes: IParsedBox[] = moovChildBoxes.filter(({ name }) => name === 'trak');
      this._logger.info(`Found ${trakBoxes.length} trak boxes`);
      this._lastKnownInitSectionNumberOfTracks = trakBoxes.length;
    }
  }

  private _parseMediaFragment(fragment: Fragment): boolean {
    if (this._lastKnownInitSectionNumberOfTracks === null) {
      return true;
    }

    const rootBoxes: IParsedBox[] = findBoxesInArrayBuffer(fragment.data as ArrayBuffer);
    const sidxBoxes: IParsedBox[] = rootBoxes.filter(({ name }) => name === 'sidx');

    this._logger.verbose(`Found following root boxes: ${rootBoxes.map(({ name }) => name).join(', ')}`);

    const moofBoxes: IParsedBox[] = rootBoxes.filter(({ name }) => name === 'moof');
    const reasons: string[] = [];
    let hasErrors: boolean = false;

    if (sidxBoxes.length > 0 && sidxBoxes.length % this._lastKnownInitSectionNumberOfTracks !== 0) {
      this._logger.warn(
        `Inconsistent media chunk: number of SIDX boxes is invalid, ${sidxBoxes.length} found, ${this._lastKnownInitSectionNumberOfTracks} expected`,
      );
      reasons.push(
        `Number of SIDX boxes (${sidxBoxes.length}) is not equal to the known amount of media tracks (${this._lastKnownInitSectionNumberOfTracks})`,
      );
      hasErrors = true;
    }

    for (const moofBox of moofBoxes) {
      const moofChildBoxes: IParsedBox[] = findBoxesInArrayBuffer(moofBox.payload as ArrayBuffer);
      const trafBoxes: IParsedBox[] = moofChildBoxes.filter(({ name }) => name === 'traf');

      this._logger.verbose(`Found following child of moof boxes: ${moofChildBoxes.map(({ name }) => name).join(', ')}`);

      if (trafBoxes.length !== this._lastKnownInitSectionNumberOfTracks) {
        this._logger.warn(
          `Inconsistent media chunk: number of TRAF boxes is invalid, ${trafBoxes.length} found, ${this._lastKnownInitSectionNumberOfTracks} expected`,
        );
        reasons.push(
          `Number of TRAF boxes (${trafBoxes.length}) is not equal to the known amount of media tracks (${this._lastKnownInitSectionNumberOfTracks})`,
        );
        hasErrors = true;
      }
    }

    if (hasErrors) {
      this._logger.warn(`Fragment ${fragment.getMsnPn()} should be reported`);
      this._eventBus.publish(FragmentsFilterEvents.problemDetected, {
        fragment: fragment.url.split('/').slice(-1)[0],
        reason: reasons.join('; '),
      });
      this._eventBus.publish(FragmentsBufferEvents.saveData, {});
    }

    return hasErrors;
  }
}
